/**
 * Shared component for rendering feature flag details
 * Used by both mobile sidebar and desktop panel
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { FEATURE_FLAG_CONFIG, getFeatureFlagValue } from '../constant';
import { getValueStyle, styles as sharedStyles } from '../utils/featureFlagStyles';

interface FlagDetailsContentProps {
  localFlags: Record<string, any>;
  editingKey: string | null;
  editValue: string;
  setEditValue: (value: string) => void;
  onSubmitValue: (key: string) => void;
  onCancelEditing: () => void;
  onStartEditing: (key: string, currentValue: any) => void;
  onResetFlag: (key: string) => void;
}

export const FlagDetailsContent: React.FC<FlagDetailsContentProps> = ({
  localFlags,
  editingKey,
  editValue,
  setEditValue,
  onSubmitValue,
  onCancelEditing,
  onStartEditing,
  onResetFlag,
}) => {
  const [expandedFlags, setExpandedFlags] = useState<Set<string>>(new Set());

  const toggleFlag = (key: string) => {
    setExpandedFlags(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      {Object.entries(FEATURE_FLAG_CONFIG).map(([key, config]) => {
        const currentValue = getFeatureFlagValue(key, localFlags);
        const isCustomValue = key in localFlags;
        const isExpanded = expandedFlags.has(key);

        return (
          <View key={key} style={styles.flagCard}>
            <TouchableOpacity
              style={styles.flagHeader}
              onPress={() => toggleFlag(key)}
            >
              <Text style={styles.flagTitle}>{config.key}</Text>
              {isExpanded ? (
                <SVG_COMPONENTS.CHEVRON_DOWN
                  width={16}
                  height={16}
                  color={ADMIN_COLORS.TEXT_SECONDARY}
                />
              ) : (
                <SVG_COMPONENTS.CHEVRON_RIGHT
                  width={16}
                  height={16}
                  color={ADMIN_COLORS.TEXT_SECONDARY}
                />
              )}
            </TouchableOpacity>

            {isExpanded && (
              <View style={styles.flagDetails}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Description:</Text>
                  <Text style={styles.detailValue}>{config.description}</Text>
                </View>

                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>
                    Current Value: {!isCustomValue && '(Using Default)'}
                  </Text>
                  <View style={[sharedStyles.valueBadge, getValueStyle(currentValue, config.defaultValue)]}>
                    <Text style={sharedStyles.valueBadgeText}>{currentValue?.toString()}</Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Default Value:</Text>
                  <View style={[sharedStyles.valueBadge, sharedStyles.valueBadgeDefault]}>
                    <Text style={sharedStyles.valueBadgeText}>{config.defaultValue.toString()}</Text>
                  </View>
                </View>

                {editingKey === key ? (
                  <View style={styles.editForm}>
                    <TextInput
                      style={styles.input}
                      value={editValue}
                      onChangeText={setEditValue}
                      placeholder={`Enter value for ${key}`}
                      placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                      autoFocus
                    />
                    <View style={styles.editButtons}>
                      <TouchableOpacity
                        style={[styles.button, styles.buttonPrimary]}
                        onPress={() => onSubmitValue(key)}
                      >
                        <Text style={styles.buttonText}>Save</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.button, styles.buttonSecondary]}
                        onPress={onCancelEditing}
                      >
                        <Text style={styles.buttonTextSecondary}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <View style={styles.actionButtons}>
                    <TouchableOpacity
                      style={[styles.button, styles.buttonSecondary]}
                      onPress={() => onResetFlag(key)}
                    >
                      <Text style={styles.buttonTextSecondary}>Restore Default</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.button, styles.buttonPrimary]}
                      onPress={() => onStartEditing(key, currentValue)}
                    >
                      <Text style={styles.buttonText}>Set Custom Value</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  flagCard: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  flagHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  flagTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    flex: 1,
  },
  flagDetails: {
    padding: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  detailRow: {
    marginBottom: 16,
  },
  detailLabel: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginBottom: 8,
  },
  detailValue: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  editForm: {
    marginTop: 8,
  },
  input: {
    backgroundColor: ADMIN_COLORS.INPUT.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.INPUT.BORDER,
    borderRadius: 4,
    padding: 12,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 12,
  },
  editButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  button: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 4,
    alignItems: 'center',
  },
  buttonPrimary: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  buttonSecondary: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  buttonTextSecondary: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
});

